import { useForm as useFormHook } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import {
  notificationSettingsSchema,
  type NotificationSettingsFormData,
} from "@core/features/profile";
import { updateProfileSettings } from "@core/features/profile/utils/updateProfileSettings";
import { usePlatformStore } from "@platform/store";
import { User } from "@platform/types/platform.types";
import { Loader } from "@themes/pollinator/ui/Loader";
import { NotificationSettingsForm } from "./NotificationSettingsForm";

/**
 * Container component for NotificationSettingsForm.
 * Handles mutations, store access, and form state management.
 */
export function NotificationSettingsFormContainer() {
  const user = usePlatformStore((s) => s.user);
  const setUser = usePlatformStore((s) => s.setUser);

  const mutation = useMutation({
    mutationFn: async (data: NotificationSettingsFormData) => {
      return updateProfileSettings({
        is_email_notifications_enabled: data.isEmailNotificationsEnabled,
        is_text_notifications_enabled: data.isTextNotificationsEnabled,
        role: user?.role || undefined,
      });
    },
    onSuccess: (data) => {
      setUser(data.user_data as unknown as User);
    },
  });

  const form = useFormHook<NotificationSettingsFormData>({
    resolver: zodResolver(notificationSettingsSchema),
    mode: "all",
    defaultValues: {
      isEmailNotificationsEnabled: user?.is_email_notifications_enabled ?? true,
      isTextNotificationsEnabled: user?.is_text_notifications_enabled ?? true,
    },
  });

  const handleSubmit = form.handleSubmit(async (data) => {
    try {
      await mutation.mutateAsync(data);
      form.reset(data);
    } catch (error) {
      form.setError("root", {
        type: "manual",
        message:
          error instanceof Error
            ? error.message
            : "Failed to update notification settings",
      });
    }
  });

  const handleCancel = () => {
    form.reset();
  };

  const { isSubmitting, isValid, isDirty } = form.formState;

  if (!user) {
    return <Loader />;
  }

  return (
    <NotificationSettingsForm
      form={form}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isDirty={isDirty}
      isValid={isValid}
      isSubmitting={isSubmitting}
    />
  );
}

export default NotificationSettingsFormContainer;
